import type { QueryClient } from '@tanstack/react-query';
import { orderKeys } from 'src/constants/orders';
import type {
  OrderBaseFieldsFragment,
  UploadedFileConnectionFieldsFragment,
} from 'src/types';

type OrderDetailCacheEntry = OrderBaseFieldsFragment & {
  slide?: unknown;
};

function hasUploadedFileNodes(
  uploadedFiles: UploadedFileConnectionFieldsFragment | null | undefined,
): boolean {
  return uploadedFiles?.edges?.some((edge) => edge?.node != null) ?? false;
}

/**
 * Writes the order detail cache after create/update/submit, preserving
 * uploadedFiles and conditional fields from the existing cache when the
 * mutation payload omits them.
 */
export function setOrderDetailCache<TOrder extends OrderBaseFieldsFragment>(
  queryClient: QueryClient,
  orderId: string,
  order: TOrder,
): void {
  queryClient.setQueryData(
    orderKeys.detail(orderId),
    (oldOrder?: OrderDetailCacheEntry) => {
      const merged: OrderDetailCacheEntry = { ...order };
      if (
        !hasUploadedFileNodes(merged.uploadedFiles) &&
        hasUploadedFileNodes(oldOrder?.uploadedFiles)
      ) {
        merged.uploadedFiles = oldOrder!.uploadedFiles;
      }
      if (!('slide' in order) && oldOrder && 'slide' in oldOrder) {
        merged.slide = oldOrder.slide;
      }
      return merged as TOrder;
    },
  );
}

/** Invalidate all order list queries. */
export function invalidateOrderLists(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: orderKeys.all });
}
